// Deciding what a force-push should actually PUT, and what a push achieved.
//
// Extracted from DatabaseWrapper so it can be unit-tested without pulling in
// pouchdb-browser, which references `self` and fails to load under node. It was
// split out of database-wrapper-passthrough.ts, which had become the shared home
// for every testable sync helper regardless of subject (deferred finding
// df-08d4a072f17b).

import { deepClone, deepCompareEQ, isPouchInternalKey, mergeConflictingDocs } from '@cf-shared/deep-helpers';
import { parseRevGeneration } from './sync-rev-and-time-utils';
import { SyncFailureClass } from './sync-failure-classification';

// #3987 A3 (audit defect D4) — forcePushDocAsync used to clone the local doc
// onto the remote's current _rev unconditionally: a last-write-wins overwrite
// that could clobber a concurrent other-device edit sitting on the remote.
// This pure decision compares the two bodies first and only overwrites when
// that is a content no-op; a diverged remote body gets a merge instead.

/** Which of {@link resolveForcePushBody}'s four cases was taken. #4407 Step 1
 *  reports this alongside the fork verdict, so the two cases that split a
 *  document and the two that do not stay separable in the telemetry instead of
 *  collapsing into one number that cannot be read. */
export type ForcePushBranch =
  /** The document does not exist on the server yet — create it fresh. */
  | 'remote-absent'
  /** The server is at a LOWER generation than the device — classic drift. */
  | 'remote-behind'
  /** The server is at or above the device and the bodies are identical. */
  | 'content-equal'
  /** The server is at or above the device and the bodies differ — merged. */
  | 'merged';

/** How forcePushDocAsync should SEND the body it was given (#4407 Step 3).
 *
 *  This chooses the TRANSPORT only. It does not decide which of
 *  {@link ForcePushBranch}'s cases was taken, and it does not change what `_rev`
 *  each branch produces — those are settled by {@link resolveForcePushBody} and
 *  are deliberately unchanged by the fix.
 *
 *  Why there are two. The original repair always did the second one, and that is
 *  what split documents: it sends the DEVICE's body while telling CouchDB the
 *  write follows the SERVER's revision, so CouchDB starts its own chain of
 *  revisions beside the device's and the document ends up with two branches. The
 *  first one sends the device's revision as it stands, together with its
 *  history, so the server adopts it into the one tree it already has. */
export type ForcePushSend =
  /** Send the device's own revision and its history, so the server joins the
   *  device's tree instead of starting a rival one. Used by the two branches
   *  that used to split the document. */
  | 'preserve-local-rev'
  /** Write the body as a new child of the server's current revision — the
   *  original behaviour. Correct for the two branches that never split
   *  anything, and the fall-back when the history cannot be shown to join. */
  | 'put-on-remote-rev';

/** Which caller asked for a force-push (#4407 Step 1). Reported so the two
 *  per-save sites and the periodic drift sweep can be told apart in the data —
 *  they fire at very different rates and on different documents.
 *
 *  `'unknown'` is the default on {@link DatabaseWrapper.forcePushDocAsync}, so a
 *  caller added later shows up as its own bucket rather than silently
 *  mislabelling itself as one of the existing three. */
export type ForcePushTrigger =
  /** workout-detail, right after a workout is saved as Done. */
  | 'workout-save-done'
  /** workout-detail, when a past workout is auto-marked Done or Skipped. */
  | 'workout-auto-conclude'
  /** The periodic checkpoint-drift sweep's recovery loop. */
  | 'drift-recovery'
  /** A caller that has not declared itself. */
  | 'unknown';

/** What {@link DatabaseWrapper.forcePushDocAsync} answers. It never throws. */
export interface ForcePushResult {
  pushed: boolean;
  /** Why the PUT failed, when it did (#4010 B4). */
  failureClass?: SyncFailureClass;
  /** Set when the PUT hit a 409 and the re-read that checks whether the server already has this
   *  revision failed too (#5191). The heal is then unconfirmed rather than known to be behind. */
  recheckFailureClass?: SyncFailureClass;
}

/** Outcome of {@link resolveForcePushBody}: what forcePushDocAsync should PUT. */
export interface ForcePushBodyDecision {
  /** The document body to PUT to the remote (with `_rev` already set, or
   *  removed when the doc does not exist remotely yet). */
  docToPut: any;
  /** True when the remote body had diverged from local and a merge was
   *  applied — the caller emits telemetry for this case. */
  mergedDivergedRemote: boolean;
  /** Which case produced `docToPut` (#4407 Step 1). Set here rather than
   *  re-derived by the caller, so there is one source of truth for it. */
  branch: ForcePushBranch;
  /** How the caller should send `docToPut` (#4407 Step 3). Set here for the
   *  same reason `branch` is: the choice belongs with the case that made it. */
  send: ForcePushSend;
}

// #4407 Step 1 — did this force-push SPLIT the document it was repairing?
//
// The background, because the rule below looks arbitrary without it. A
// force-push sends the DEVICE's body but tells CouchDB the write follows the
// SERVER's revision. CouchDB then starts its own chain of revisions while the
// device keeps its own, and the two can never coincide: every local revision id
// is a random UUID (pouchdb-utils' rev()), while CouchDB computes its own. So
// the document ends up with two branches — the app shows one and hides the
// other. That hidden half is what "fork" means here.
//
// Only two of the four cases do that, and the distinction is the entire point of
// this measurement. "The write followed something other than the local revision"
// is NOT the rule — it is too broad. On the content-equal and merged cases the
// server's revision is normally a DESCENDANT of the device's, so the write
// extends a single chain instead of splitting it. Counting those would inflate
// the baseline that the eventual fix is judged against.
//
// Two arguments are enough even though there are four cases. The content-equal
// and merged cases both present a parent at or above the local generation, so
// this function cannot tell them apart — and it does not need to, because both
// answer "no split caused here". ForcePushBranch is what keeps them separable.
/**
 * True when a landed force-push left the document with a second, hidden branch.
 *
 * @param localRev the revision the device holds.
 * @param putParentRev the revision the write claimed to follow, or `null` when
 *   the document was absent from the server and the write created it fresh.
 */
export function didForcePushFork(localRev: string, putParentRev: string | null | undefined): boolean {
  // no parent: the server mints its own generation 1 while the device keeps the
  // generation 1 it made itself — two unrelated branches of the same document
  if (!putParentRev) return true;
  // parent older than the device's revision: the write re-parents onto an
  // ancestor of the local leaf, so it grows a branch beside it (classic drift)
  return parseRevGeneration(putParentRev) < parseRevGeneration(localRev);
}

// The rule that decides whether a 409 on a force-push was a real failure
// (df-0b024a455511). It lives here, rather than inline in the catch block that
// uses it, so it can be checked by CALLING it: `database-wrapper.ts` cannot be
// imported under node — it pulls in pouchdb-browser, which references `self` —
// so anything left inline there can only ever be pinned by matching source text.
//
// What the rule means. A force-push that comes back 409 has usually already
// arrived by another route: normal live-sync replication delivered the document
// while this direct write was in flight. The caller re-reads the remote, and if
// the server has caught up there is no local-ahead drift left for this repair to
// recover, so the push is reported as a success (Sentry 3ZD race-409 false
// positive).
//
// NOTE what this deliberately does NOT assert: that the server holds *our*
// content. A concurrent write from another device at a higher generation
// satisfies it too. That is an ordinary sync conflict, resolved on the next
// pull — not checkpoint drift, which is the only thing the force-push exists to
// repair.
/**
 * True when a force-push 409 means the server has already reached or passed the
 * revision generation this device holds, so there is no local-ahead drift left
 * to recover and the push counts as a success.
 *
 * @param localGeneration the generation of the revision the device holds, as
 *   {@link parseRevGeneration} reads it.
 * @param remoteGeneration the generation the server is on after the conflict was
 *   re-read, read the same way.
 *
 * Both arguments come from `parseRevGeneration`, which answers `0` for a
 * revision it cannot read. Two unreadable revisions therefore compare equal and
 * answer `true` — that is the pre-existing behaviour of the inline comparison
 * this replaces, kept deliberately: PouchDB does not hand out a malformed `_rev`,
 * and changing the answer here would be a behaviour change smuggled into a move.
 */
export function isCaughtUp409(localGeneration: number, remoteGeneration: number): boolean {
  return remoteGeneration >= localGeneration;
}

// #4407 Step 3 — may the device's own revision be pushed, or would that land a
// SECOND branch instead of joining the server's tree?
//
// The background. The preserving push sends the document with its `_revisions`
// history and asks CouchDB not to make a new revision for it. CouchDB grafts that
// history onto the tree it already holds — but ONLY if the history actually
// contains the revision the server is currently on. When it does not, CouchDB
// stores the pushed path as a separate branch AND REPORTS SUCCESS. That is the
// exact failure this whole ticket exists to remove, arriving silently, so the
// question has to be answered before the push rather than discovered after it.
//
// This can genuinely happen: PouchDB prunes local history at `revs_limit`, and
// production has documents past generation 4300, so a device whose history has
// been trimmed cannot reach back to a server that is far behind.
//
// COMPARE THE HASH, NOT THE GENERATION — this is the part that is easy to get
// wrong and expensive to get wrong. Asking only "is the server's generation
// inside my span?" passes a server sitting at a covered generation with a
// DIFFERENT hash, which is precisely a server on another branch, which is
// precisely the case that lands a second branch.
/**
 * True when the local `_revisions` history provably reaches the server's current
 * revision, so a `new_edits: false` push joins one tree instead of splitting it.
 *
 * @param revisions the local document's `_revisions`, as returned by
 *   `get(id, { revs: true })`. `{ start, ids }` where `ids[0]` is the leaf's hash
 *   and each later entry is one generation older — so generation `g` sits at
 *   `ids[start - g]`.
 * @param remoteRev the revision the server currently holds, or `null`/`undefined`
 *   when the document is not on the server at all.
 *
 * Answers `true` for an absent `remoteRev`: there is no tree to join, so the
 * create cannot split anything. Answers `false` for anything it cannot check —
 * a missing history, an out-of-range generation — because "I cannot prove this
 * joins" must send the caller down the old path rather than push blind.
 */
export function localHistoryReachesRemoteRev(
  revisions: { start: number, ids: string[] } | undefined | null,
  remoteRev: string | null | undefined,
): boolean {
  // nothing on the server to join to — the push creates the document
  if (!remoteRev) return true;
  // no history to reason from: fail closed rather than guess
  if (!revisions || !Array.isArray(revisions.ids) || revisions.ids.length === 0) return false;
  const separatorIndex = remoteRev.indexOf('-');
  if (separatorIndex <= 0) return false;
  const remoteGeneration = parseRevGeneration(remoteRev);
  const remoteHash = remoteRev.slice(separatorIndex + 1);
  // ids[0] is the leaf at `start`, so walking back one entry is one generation
  const indexOfRemoteGeneration = revisions.start - remoteGeneration;
  if (indexOfRemoteGeneration < 0 || indexOfRemoteGeneration >= revisions.ids.length) return false;
  return revisions.ids[indexOfRemoteGeneration] === remoteHash;
}

/** Drop the fields that must not take part in the content comparison: every
 *  `_`-prefixed PouchDB internal except `_id` (notably `_rev`, which differs
 *  by definition here) AND the `db__*` app bookkeeping fields (`db__trans`
 *  changes on every save, so comparing it would flag content-identical docs
 *  as diverged). NOTE: this is deliberately NOT the same strip shape as
 *  applyPulledDocToCache (which keeps `_rev` and `db__*`) — this one exists
 *  purely to answer "is the USER CONTENT equal?". */
function stripNonContentFieldsForCompare(doc: any): any {
  const out: any = {};
  for (const key of Object.keys(doc)) {
    if (key !== '_id' && isPouchInternalKey(key)) continue;
    out[key] = doc[key];
  }
  return out;
}

/**
 * Decide what body a force-push should PUT to the remote (#3987 A3).
 *
 * - Remote doc absent: create it fresh from the local body (no `_rev`).
 * - Remote generation BEHIND local: this is classic checkpoint drift — the
 *   remote body is (in the overwhelmingly common case) a strict ANCESTOR of
 *   local, an older snapshot of the same edit chain. Plain overwrite, exactly
 *   the pre-A3 behavior. Merging here would be WRONG: mergeConflictingDocs
 *   keeps the longer array / non-null primitive, so merging against an
 *   ancestor resurrects data the user's newest edits deleted.
 * - Remote generation AT or ABOVE local with EQUAL content (compared without
 *   `_*`/`db__*` fields): overwrite is a content no-op — keep it, no telemetry.
 * - Remote generation AT or ABOVE local with DIFFERENT content: genuine
 *   divergence — the remote holds a concurrent edit (same-gen split brain, or
 *   another device got ahead) that a plain overwrite would clobber (defect
 *   D4). Merge instead: `mergeConflictingDocs(local, remote)` with the LOCAL
 *   doc as doc1, so local keeps its `_*`/`db__*` identity fields while
 *   remote-only additions survive. Primitive ties go to whichever side's
 *   top-level `db__trans` write timestamp is newer (internal #42); when
 *   recency is unknown the local doc wins, as before.
 *
 * KNOWN LIMITATION (do not oversell this as lossless): mergeConflictingDocs
 * is a heuristic, biased merge — arrays keep the LONGER side wholesale (the
 * shorter side's unique elements are dropped) and primitive ties go to the
 * newer-written doc (falling back to local when recency is unknown). And
 * because db__trans stamps the DOC's last write, not each field's, the newer
 * doc's UNTOUCHED (ancestor-valued) fields win their ties too — so when two
 * devices edited different fields, the older device's genuine edit can still
 * lose (#42 relocated the clobber away from stale pushes; it did not
 * eliminate it). This turns a blind clobber into a recency-aware merge for
 * the genuinely-diverged cases, but it is NOT a true field-level reconcile.
 */
export function resolveForcePushBody(localDoc: any, remoteDoc: any | null): ForcePushBodyDecision {
  const docToPut = deepClone(localDoc);
  if (!remoteDoc || !remoteDoc._rev) {
    // doc doesn't exist on remote yet — create it
    delete docToPut._rev;
    return { docToPut, mergedDivergedRemote: false, branch: 'remote-absent', send: 'preserve-local-rev' };
  }
  // if the remote is at a LOWER generation than local, it is an older snapshot
  // of our own edit chain (classic drift) — overwrite, never merge (see above).
  //
  // #4407 Step 3: this branch used to do `docToPut._rev = remoteDoc._rev` here,
  // and that single line was the bug. It told CouchDB the write followed the
  // SERVER's revision while carrying the DEVICE's body, so CouchDB wrote its own
  // revision under its own chain while the device kept its own — two branches of
  // one document, one of which the app then hides. Measured in production over
  // the 7 days to 2026-08-25: 505 of 505 pushes taking this branch split their
  // document. The local `_rev` is now left alone, and `send` tells the caller to
  // push it (with its history) instead of re-parenting it.
  if (parseRevGeneration(remoteDoc._rev) < parseRevGeneration(localDoc._rev)) {
    return { docToPut, mergedDivergedRemote: false, branch: 'remote-behind', send: 'preserve-local-rev' };
  }
  if (deepCompareEQ(stripNonContentFieldsForCompare(localDoc), stripNonContentFieldsForCompare(remoteDoc))) {
    // content is equal — overwriting the remote _rev is a content no-op
    docToPut._rev = remoteDoc._rev;
    return { docToPut, mergedDivergedRemote: false, branch: 'content-equal', send: 'put-on-remote-rev' };
  }
  // reuse the clone from the top as the merge input (mergeConflictingDocs reads
  // doc1 without mutating it, but passing the clone keeps the identity-mapped
  // localDoc provably untouched without cloning a second time)
  const merged: any = mergeConflictingDocs(docToPut, remoteDoc);
  merged._rev = remoteDoc._rev;
  return { docToPut: merged, mergedDivergedRemote: true, branch: 'merged', send: 'put-on-remote-rev' };
}

/**
 * What a one-shot push to the server actually did.
 *
 * `pushToRemoteAsync` exists to get local writes onto the server before the app
 * reloads or navigates away — the coach-invite claim is its reason for existing.
 * It used to answer `Promise<void>`, so a caller could not tell a completed push
 * from one that never ran, and every way it can decline to run is a plain
 * `console.log` (#4801). That mattered: a boot that failed leaves the wrapper
 * with no remote database at all, so the push silently did nothing while the
 * claim went on to tell the client they were connected to their coach. The
 * coach's program then existed only on that device, where the next sign-out
 * wiped it.
 */
export enum PushToRemoteOutcome {
  /** The push ran to completion. This is the only outcome that reached the server. */
  Pushed = 'pushed',
  /** There was no local or no remote database to push between. */
  SkippedNoDatabase = 'skipped: no local or remote database',
  /** The wrapper is shutting down, so no read lock could be taken. */
  SkippedShuttingDown = 'skipped: shutting down',
  /** The push started and was still running when the time limit ran out. */
  TimedOut = 'timed out',
  /**
   * The replication itself failed — no network, an expired session, a remote that
   * refused or could not be reached.
   *
   * This one is easy to lose, and losing it puts the whole bug back. The helper that
   * runs the replication reports a REJECTED promise as "did not time out" and hands
   * back a separate `succeeded: false`, so code that looks only at the timeout flag
   * reads a failed push as a completed one — which is the most common real failure
   * after having no remote database at all.
   */
  Failed = 'failed',
}

/**
 * Did the data a caller just wrote actually reach the server?
 *
 * Split out from the caller so the answer is one testable rule rather than a
 * condition spelled out at each call site. Only a completed push counts: a
 * timeout is NOT a success, because the push may have sent nothing at all, and
 * a caller about to tell someone their setup is finished must not say so on a
 * maybe.
 */
export function didPushReachServer(outcome: PushToRemoteOutcome): boolean {
  return outcome === PushToRemoteOutcome.Pushed;
}
